import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { useAuth } from '../../AuthContext'
import ModalOverlay from './ModalOverlay'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TEXTAREA = 'w-full resize-y rounded-lg border border-border-input bg-surface px-2.5 py-1.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TH = 'text-secondary border-b border-secondary px-2.5 py-1.5 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-1.5 align-middle'
const LABEL = 'text-secondary text-[11px] font-medium'
const FIELD = 'flex flex-col gap-1'

// フィードバック管理（仕様書 3.7 / メニュー⑦）
export default function AIPurchaseAssistFeedback() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()
  const [days, setDays] = useState(30)
  const [evaluation, setEvaluation] = useState('thumbs_down')
  const [status, setStatus] = useState('')
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  const [totalPages, setTotalPages] = useState(1)
  const [summary, setSummary] = useState(null)
  const [patterns, setPatterns] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedId, setSelectedId] = useState(null)

  const fetchSummary = async () => {
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/feedback/summary?days=${days}`, { headers: authHeaders() })
      if (r.ok) setSummary(await r.json())
    } catch (e) {/* ignore */}
  }
  const fetchPatterns = async () => {
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/feedback/patterns?days=${days}&min_occurrences=2`, { headers: authHeaders() })
      if (r.ok) setPatterns(await r.json())
    } catch (e) {/* ignore */}
  }
  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ days: String(days), page: String(page), per_page: String(perPage) })
      if (evaluation) params.set('evaluation', evaluation)
      if (status) params.set('status', status)
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/feedback?${params}`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setItems(d.items || [])
      setTotal(d.total || 0)
      setTotalPages(d.total_pages || 1)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [siteId, days, evaluation, status, page])
  useEffect(() => { fetchSummary(); fetchPatterns() }, [siteId, days])

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='📮 フィードバック管理'
        description='ユーザー評価（👍/👎）の集約と改善ループ管理。問題のAI応答を発見 → 原因特定 → 改善画面へ遷移までを完結させます。'
      />

      {/* KPIサマリー */}
      {summary && (
        <div className='mb-3.5 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2.5'>
          <KpiCard label='満足度' value={`${summary.satisfaction_rate.toFixed(1)}%`} sub={`👍${summary.thumbs_up} / 👎${summary.thumbs_down}`} color='var(--color-success)' />
          <KpiCard label='対応待ち' value={summary.by_status?.untouched ?? 0} sub='未着手の👎・問題報告' color='var(--color-critical)' />
          <KpiCard label='対応中' value={summary.by_status?.in_progress ?? 0} sub='対応プロセス中' color='var(--color-warning)' />
          <KpiCard label='解決率' value={`${summary.resolution_rate.toFixed(1)}%`} sub={`完了+破棄 / 対応必要`} color='var(--color-fill-brand)' />
        </div>
      )}

      {/* パターン検出 */}
      {patterns && patterns.patterns && patterns.patterns.length > 0 && (
        <div className={`${CARD} mb-3.5 p-3.5`} style={{ borderLeft: '3px solid var(--color-warning)' }}>
          <h3 className='text-warning m-0 mb-2 text-[13px] font-bold'>
            🔍 問題パターン（{patterns.patterns.length} 件）
            <span className='text-tertiary ml-2 text-[10px] font-normal'>
              👎が複数集中しているクエリ
            </span>
          </h3>
          <div className='flex flex-col gap-1.5'>
            {patterns.patterns.slice(0, 5).map((p, i) => (
              <div key={i} className='bg-surface-secondary rounded-md border border-secondary p-2'>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                  <div className='text-primary flex-1 text-xs'>
                    <strong className='text-warning mr-1.5'>{p.count}件 👎</strong>
                    {p.sample_question}
                  </div>
                  <div className='flex gap-1.5'>
                    {p.untouched_count > 0 && (
                      <span className='bg-surface-warning text-warning rounded px-2 py-px text-[10px] font-bold'>
                        未対応 {p.untouched_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* フィルター */}
      <div className={`${CARD} mb-3 flex flex-wrap items-center gap-1.5 p-2.5`}>
        <select value={days} onChange={e => { setDays(parseInt(e.target.value)); setPage(1) }} className={INPUT}>
          <option value={7}>過去7日</option>
          <option value={30}>過去30日</option>
          <option value={90}>過去90日</option>
        </select>
        <select value={evaluation} onChange={e => { setEvaluation(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>評価すべて</option>
          <option value='thumbs_down'>👎・問題報告</option>
          <option value='problem'>⚠ 問題報告のみ</option>
          <option value='thumbs_up'>👍のみ</option>
        </select>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>対応すべて</option>
          <option value='untouched'>📥 未対応</option>
          <option value='in_progress'>🔄 対応中</option>
          <option value='resolved'>✅ 解決</option>
          <option value='dismissed'>⛔ 破棄</option>
        </select>
        <Button color={ButtonColor.SECONDARY} onClick={fetchAll}>🔄 更新</Button>
      </div>

      {error && <div className='bg-surface-critical text-critical mb-2.5 rounded-lg p-2.5 text-xs'>{error}</div>}

      {/* 一覧 */}
      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : items.length === 0 ? (
        <div className={`${CARD} text-secondary p-8 text-center text-xs`}>
          該当するフィードバックがありません
        </div>
      ) : (
        <>
          <div className={`${CARD} overflow-x-auto p-0`}>
            <table className='w-full border-collapse text-xs'>
              <thead>
                <tr className='bg-surface-secondary'>
                  <th className={TH}>日時</th>
                  <th className={TH}>評価</th>
                  <th className={TH}>AI応答（先頭）</th>
                  <th className={TH}>モデル</th>
                  <th className={TH}>対応</th>
                  <th className={TH}>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(m => {
                  const STATUS_BADGE = {
                    untouched: { className: 'bg-surface-warning text-warning', label: '📥 未対応' },
                    in_progress: { className: 'bg-surface-emphasis text-emphasis', label: '🔄 対応中' },
                    resolved: { className: 'bg-surface-success text-success', label: '✅ 解決' },
                    dismissed: { className: 'bg-surface-secondary text-secondary', label: '⛔ 破棄' },
                  }
                  const sb = STATUS_BADGE[m.feedback_status || 'untouched'] || STATUS_BADGE.untouched
                  return (
                    <tr key={m.message_id} className='border-b border-secondary'>
                      <td className={`${TD} text-secondary text-[10px]`}>
                        {m.created_at ? new Date(m.created_at).toLocaleString('ja-JP') : '-'}
                      </td>
                      <td className={TD}>
                        {m.user_evaluation === 'thumbs_up' ? '👍' : m.user_evaluation === 'thumbs_down' ? '👎' : m.conv_problem_flag ? '' : '-'}
                        {m.conv_problem_flag && <span title='問題報告された会話'>⚠</span>}
                      </td>
                      <td className={`${TD} max-w-[360px] overflow-hidden text-ellipsis whitespace-nowrap`} title={m.content}>
                        {m.content || '-'}
                      </td>
                      <td className={`${TD} text-secondary font-mono text-[10px]`}>{m.llm_model || '-'}</td>
                      <td className={TD}>
                        <span className={`${sb.className} rounded px-2 py-0.5 text-[10px] font-bold`}>
                          {sb.label}
                        </span>
                        {m.feedback_action_taken && (
                          <span className='bg-surface-emphasis text-emphasis ml-1 rounded px-1.5 py-px text-[9px] font-bold'>
                            {m.feedback_action_taken}
                          </span>
                        )}
                      </td>
                      <td className={TD}><Button color={ButtonColor.SECONDARY} onClick={() => setSelectedId(m.message_id)}>確認</Button></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className='mt-3 flex items-center justify-center gap-1.5 text-xs'>
            <Button color={ButtonColor.SECONDARY} disabled={page <= 1} onClick={() => setPage(Math.max(1, page - 1))}>前へ</Button>
            <span className='text-secondary'>{page} / {totalPages} （計 {total} 件）</span>
            <Button color={ButtonColor.SECONDARY} disabled={page >= totalPages} onClick={() => setPage(page + 1)}>次へ</Button>
          </div>
        </>
      )}

      {selectedId && <FeedbackDetail siteId={siteId} messageId={selectedId} authHeaders={authHeaders} onClose={() => setSelectedId(null)} onChanged={fetchAll} />}
    </Main>
  )
}


// =====================
// 詳細オーバーレイ
// =====================
function FeedbackDetail({ siteId, messageId, authHeaders, onClose, onChanged }) {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState({ feedback_status: '', feedback_action_taken: '', feedback_internal_note: '' })
  const [saving, setSaving] = useState(false)

  const reload = () => {
    setLoading(true)
    fetch(`/api/sites/${siteId}/purchase-assist/feedback/${messageId}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        setData(d)
        setDraft({
          feedback_status: d.feedback?.feedback_status || 'untouched',
          feedback_action_taken: d.feedback?.feedback_action_taken || '',
          feedback_internal_note: d.feedback?.feedback_internal_note || '',
        })
      })
      .finally(() => setLoading(false))
  }
  useEffect(() => { reload() }, [messageId])

  // 2026-10-26 修正（SMART_HUB_DEV-195）：送る内容を引数で受け取り、成否を返す。
  //   setDraft の直後に呼ぶと state が反映前で、古いステータスが送られていたため
  const save = async (body = draft) => {
    setSaving(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/feedback/${messageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || `HTTP ${r.status}`)
      }
      onChanged && onChanged()
      reload()
      return true
    } catch (e) {
      alert(`保存失敗: ${e.message}`)
      return false
    } finally {
      setSaving(false)
    }
  }

  // 2026-10-26 修正（SMART_HUB_DEV-195）：「対応中」の保存が終わってから移動する（失敗したら移動しない）
  const saveAsInProgressAndGo = async (actionTaken, path) => {
    const body = { ...draft, feedback_status: 'in_progress', feedback_action_taken: actionTaken }
    setDraft(body)
    if (await save(body)) navigate(path)
  }

  const goToPromptManagement = () => {
    if (window.confirm('AIプロファイル管理画面に移動します（フィードバックは「対応中」に変更されます）。')) {
      saveAsInProgressAndGo('prompt_updated', `/sites/${siteId}/ai-purchase-assist/ai-profiles`)
    }
  }
  const goToNgRules = () => {
    if (window.confirm('NGワード管理画面に移動します（フィードバックは「対応中」に変更されます）。')) {
      saveAsInProgressAndGo('ng_added', `/sites/${siteId}/ai-purchase-assist/ng-rules`)
    }
  }

  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-start justify-center bg-black/60 p-8 pb-20 backdrop-blur-sm'>
      <div onClick={e => e.stopPropagation()} className={`${CARD} max-h-[90vh] w-full max-w-[820px] overflow-auto p-4`}>
        <div className='mb-3 flex items-center justify-between'>
          <h3 className='text-emphasis m-0 text-sm font-bold'>📮 フィードバック詳細 #{messageId}</h3>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕</Button>
        </div>

        {loading || !data ? (
          <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
        ) : (
          <>
            {/* 会話表示（対象メッセージをハイライト） */}
            <div className='mb-3.5'>
              <h4 className='text-emphasis m-0 mb-1.5 text-xs font-bold'>💬 元の会話</h4>
              <div className='bg-surface-secondary flex max-h-[280px] flex-col gap-1.5 overflow-y-auto rounded-md p-2.5'>
                {(data.conversation_messages || []).map(m => {
                  const bubble = m.is_target
                    ? { className: 'bg-surface-critical', border: 'var(--color-critical)' }
                    : m.role === 'user'
                      ? { className: 'bg-surface-emphasis', border: 'var(--color-fill-brand)' }
                      : { className: 'bg-surface-success', border: 'var(--color-success)' }
                  return (
                    <div key={m.id} className={`${bubble.className} rounded-md p-2`} style={{ borderLeft: `3px solid ${bubble.border}` }}>
                      <div className='text-tertiary mb-1 text-[9px]'>
                        {m.role === 'user' ? '👤 ユーザー' : '🤖 AI'}
                        {m.is_target && <span className='text-critical ml-1.5 font-bold'>← フィードバック対象</span>}
                        {m.user_evaluation === 'thumbs_up' && <span className='ml-1.5'>👍</span>}
                        {m.user_evaluation === 'thumbs_down' && <span className='ml-1.5'>👎</span>}
                      </div>
                      <div className='text-primary text-xs whitespace-pre-wrap'>{m.content}</div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* ユーザーフィードバックメモ */}
            {data.feedback?.user_feedback_note && (
              <div className='bg-surface-warning mb-3.5 rounded-md border border-secondary p-2.5'>
                <div className='text-warning mb-1 text-[11px] font-bold'>ユーザーからのコメント</div>
                <div className='text-primary text-xs'>{data.feedback.user_feedback_note}</div>
              </div>
            )}

            {/* 対応ステータス更新 */}
            <div className={`${CARD} mb-3 p-3`} style={{ borderLeft: '3px solid var(--color-fill-brand)' }}>
              <h4 className='text-emphasis m-0 mb-2.5 text-xs font-bold'>📋 対応管理</h4>
              <div className='grid grid-cols-2 gap-2.5'>
                <Field label='ステータス'>
                  <select value={draft.feedback_status} onChange={e => setDraft({ ...draft, feedback_status: e.target.value })} className={INPUT}>
                    <option value='untouched'>📥 未対応</option>
                    <option value='in_progress'>🔄 対応中</option>
                    <option value='resolved'>✅ 解決済</option>
                    <option value='dismissed'>⛔ 破棄（対応不要）</option>
                  </select>
                </Field>
                <Field label='対応アクション'>
                  <select value={draft.feedback_action_taken} onChange={e => setDraft({ ...draft, feedback_action_taken: e.target.value })} className={INPUT}>
                    <option value=''>未指定</option>
                    <option value='prompt_updated'>プロンプト修正</option>
                    <option value='ng_added'>NGリスト追加</option>
                    <option value='faq_created'>FAQ追加</option>
                    <option value='config_changed'>設定変更</option>
                    <option value='ignored'>対応不要</option>
                    <option value='other'>その他</option>
                  </select>
                </Field>
              </div>
              <Field label='対応メモ（運用者向け）'>
                <textarea
                  value={draft.feedback_internal_note}
                  onChange={e => setDraft({ ...draft, feedback_internal_note: e.target.value })}
                  rows={2}
                  placeholder='対応内容や経緯のメモ'
                  className={TEXTAREA}
                />
              </Field>
              <div className='mt-2.5 flex flex-wrap justify-between gap-1.5'>
                <div className='flex gap-1.5'>
                  <Button color={ButtonColor.SECONDARY} onClick={goToPromptManagement}>🎭 AIプロファイル管理へ</Button>
                  <Button color={ButtonColor.SECONDARY} onClick={goToNgRules}>🚫 NGリストへ</Button>
                </div>
                <Button color={ButtonColor.PRIMARY} disabled={saving} onClick={() => save()}>{saving ? '保存中...' : '✓ 保存'}</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  )
}


function KpiCard({ label, value, sub, color }) {
  return (
    <div className={`${CARD} p-3`} style={{ borderLeft: `3px solid ${color}` }}>
      <div className='text-secondary text-[10px]'>{label}</div>
      <div className='text-primary mt-0.5 text-lg font-bold'>{value}</div>
      {sub && <div className='text-tertiary mt-0.5 text-[9px]'>{sub}</div>}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div className={`${FIELD} mb-2`}>
      <label className={LABEL}>{label}</label>
      {children}
    </div>
  )
}
